import { useAppData } from "@/contexts/AppDataContext";
import { cn } from "@/lib/utils";
import { ChevronDown, Search } from "lucide-react";
import * as React from "react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { CallQueueType } from "@/constants/queue";

interface QueueFilterProps {
  value: string | string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
}

export const QueueFilter: React.FC<QueueFilterProps> = ({
  value,
  onChange,
  className
}) => {
  const allQueueLabel = "All Call Queues";
  const { queues } = useAppData();

  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  // Ensure value is always an array
  const selectedValues = React.useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Filter queues based on search term
  const filteredQueues = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return queues;
    }

    const searchLower = searchTerm.toLowerCase();
    return queues.filter(queue =>
      queue.label.toLowerCase().includes(searchLower)
    );
  }, [queues, searchTerm]);

  const handleValueToggle = (microsoftId: string) => {
    if (microsoftId === "") {
      onChange([]);
      return;
    }

    const newValue = selectedValues.includes(microsoftId)
      ? selectedValues.filter((v) => v !== microsoftId)
      : [...selectedValues, microsoftId];

    onChange(newValue);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setOpen(false);
      setSearchTerm("");
    } else {
      setOpen(true);
    }
  };

  // Get selected labels for display
  const selectedLabels = React.useMemo(() => {
    const labels: string[] = [];

    selectedValues.forEach(value => {
      const queue = queues.find(q => q.microsoftId === value);
      if (queue) {
        labels.push(queue.label);
      }
    });

    return labels.join(", ");
  }, [selectedValues, queues]);

  const getPlaceholder = () => {
    if (selectedValues.length === 0) {
      return allQueueLabel;
    }
    return selectedLabels;
  };

  return (
    <div className="flex items-center gap-2 w-full min-w-[180px] md:w-[230px]">
      <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "relative flex items-center w-full bg-[#F5F6F7] dark:bg-[#0c3451] rounded-xl px-4 py-1.5 font-bold text-primary dark:text-white text-sm focus:outline-none border-0 shadow-none h-[36px] hover:bg-[#e9eaeb] dark:hover:bg-[#2C4C63] transition justify-between",
              className
            )}
            style={{ boxShadow: "none" }}
          >
            <span className="truncate text-left text-sm w-full pr-6 text-primary dark:text-white">
              {getPlaceholder()}
            </span>
            <ChevronDown className="w-4 h-4 text-primary dark:text-white absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="max-w-[400px] p-0 text-sm bg-white dark:bg-[#3C5C73] max-h-[400px] overflow-hidden" align="start">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search call queues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm bg-gray-50 dark:bg-[#3C5C73] border-gray-200 dark:border-white text-primary dark:text-white placeholder-gray-500"
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
              />
            </div>
          </div>

          {/* Scrollable Options */}
          <div className="max-h-[300px] overflow-y-auto">
            <div className="p-2">
              {/* All Queues option */}
              <DropdownMenuCheckboxItem
                key="all"
                checked={selectedValues.length === 0}
                onCheckedChange={() => handleValueToggle("")}
                onSelect={(e) => {
                  e.preventDefault();
                }}
                className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer font-medium"
              >
                {allQueueLabel}
              </DropdownMenuCheckboxItem>

              {/* Individual Queues */}
              {filteredQueues.map((queue) => (
                <DropdownMenuCheckboxItem
                  key={`queue-${queue.microsoftId}`}
                  checked={selectedValues.includes(queue.microsoftId)}
                  onCheckedChange={() => handleValueToggle(queue.microsoftId)}
                  onSelect={(e) => {
                    e.preventDefault();
                  }}
                  className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer"
                >
                  {queue.label}
                </DropdownMenuCheckboxItem>
              ))}

              {/* No results message */}
              {filteredQueues.length === 0 && searchTerm.trim() && (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No call queues found
                </div>
              )}
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
